import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { fmt, todayMonth } from '../lib/format';
import { ChartDonut, TrendUp, TrendDown, Minus, ChartBar, ArrowsLeftRight, CalendarBlank } from '@phosphor-icons/react';

export default function Report() {
  const todayStr = new Date().toISOString().slice(0, 10);
  const monthStart = todayStr.slice(0, 7) + '-01';

  const [mode, setMode] = useState('month'); // 'month' | 'range'
  const [month, setMonth] = useState(todayMonth());
  const [startDate, setStartDate] = useState(monthStart);
  const [endDate, setEndDate] = useState(todayStr);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('overview');

  const load = () => {
    setLoading(true);
    const req = mode === 'month'
      ? api.get('/reports/monthly', { params: { month } })
      : api.get('/reports/range', { params: { start: startDate, end: endDate } });
    req.then(r => { setData(r.data); setLoading(false); })
       .catch(() => setLoading(false));
  };

  useEffect(() => { if (mode === 'month') load(); /* eslint-disable-next-line */ }, [mode, month]);
  useEffect(() => { if (mode === 'range') load(); /* eslint-disable-next-line */ }, [mode]);

  if (loading || !data) return <div className="card-base p-12 text-center text-slate-500">加载中…</div>;

  const cur = data.current;
  const prev = data.previous;
  const cmp = data.comparison;

  const usdtToMyr = cur.weighted_avg_cost_rate > 0 ? cur.weighted_avg_cost_rate : 4.1;
  const orderProfitMyr = cur.order_profit_usdt * usdtToMyr;
  const bankProfitMyr = cur.bank_profit_usdt * usdtToMyr;
  const grossProfitMyr = orderProfitMyr + bankProfitMyr;
  const netProfitMyr = grossProfitMyr - cur.expense_total_myr;

  const periodLabel = mode === 'month' ? data.month : `${data.start} ~ ${data.end}`;
  const prevPeriodLabel = mode === 'month' ? data.previous_month : `${data.previous_start} ~ ${data.previous_end}`;

  return (
    <div className="space-y-5 fade-in max-w-5xl" data-testid="report-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">财务报表</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            {periodLabel} · 对比 <span className="text-slate-300">{prevPeriodLabel}</span>
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="inline-flex rounded-lg bg-slate-900/50 border border-slate-800 p-1">
            <button onClick={() => setView('overview')}
                    data-testid="view-overview"
                    className={`px-3 py-1.5 text-sm rounded-md transition-all flex items-center gap-1.5 ${
                      view === 'overview' ? 'bg-indigo-600 text-white font-medium' : 'text-slate-400 hover:text-white'
                    }`}>
              <ChartBar size={14} weight="duotone" /> 概览
            </button>
            <button onClick={() => setView('compare')}
                    data-testid="view-compare"
                    className={`px-3 py-1.5 text-sm rounded-md transition-all flex items-center gap-1.5 ${
                      view === 'compare' ? 'bg-indigo-600 text-white font-medium' : 'text-slate-400 hover:text-white'
                    }`}>
              <ArrowsLeftRight size={14} weight="duotone" /> 详细对比
            </button>
          </div>
        </div>
      </div>

      {/* Period selector */}
      <div className="card-base p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="report-period">
        <div className="inline-flex rounded-lg bg-slate-900/50 border border-slate-800 p-1">
          <button
            onClick={() => setMode('month')}
            data-testid="report-mode-month"
            className={`px-3 py-1.5 text-sm rounded-md transition-all flex items-center gap-1.5 ${
              mode === 'month' ? 'bg-indigo-600 text-white font-medium' : 'text-slate-400 hover:text-white'
            }`}
          >
            <CalendarBlank size={14} weight="duotone" /> 按月
          </button>
          <button
            onClick={() => setMode('range')}
            data-testid="report-mode-range"
            className={`px-3 py-1.5 text-sm rounded-md transition-all flex items-center gap-1.5 ${
              mode === 'range' ? 'bg-indigo-600 text-white font-medium' : 'text-slate-400 hover:text-white'
            }`}
          >
            <CalendarBlank size={14} weight="duotone" /> 自定义日期
          </button>
        </div>

        {mode === 'month' ? (
          <input
            type="month"
            className="input-base font-num text-sm py-1.5 max-w-[160px]"
            value={month}
            onChange={e => setMonth(e.target.value)}
            data-testid="report-month"
          />
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-1">
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={e => setStartDate(e.target.value)}
              className="input-base font-num text-sm py-1.5 max-w-[160px]"
              data-testid="report-start-date"
            />
            <span className="text-slate-500 text-sm">至</span>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={todayStr}
              onChange={e => setEndDate(e.target.value)}
              className="input-base font-num text-sm py-1.5 max-w-[160px]"
              data-testid="report-end-date"
            />
            <button
              onClick={load}
              className="btn-primary text-sm py-1.5 px-4"
              data-testid="report-apply-range"
            >应用</button>
            {data.days && (
              <span className="text-xs text-slate-500">({data.days} 天 vs 上一周期)</span>
            )}
          </div>
        )}
      </div>

      {view === 'compare' && (
        <CompareView cur={cur} prev={prev} cmp={cmp} curMonth={periodLabel} prevMonth={prevPeriodLabel} />
      )}

      {view === 'overview' && (
        <>
          {/* 营业额 Section */}
          <div className="card-base p-5 sm:p-6">
            <h2 className="text-lg font-semibold text-white mb-4">📈 营业额</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <KPICard label="订单笔数" value={cur.order_count} suffix="笔" change={cmp.order_count} previous={prev.order_count} />
              <KPICard label="活跃客户" value={cur.unique_customers} suffix="位" change={cmp.unique_customers} previous={prev.unique_customers} accent="cyan" />
              <KPICard label="总收 MYR" value={fmt(cur.total_myr)} suffix="MYR" change={cmp.total_myr} previous={prev.total_myr} accent="amber" />
              <KPICard label="总收 USDT (直收)" value={fmt(cur.total_usdt_in)} suffix="USDT" change={cmp.total_usdt_in} previous={prev.total_usdt_in} accent="emerald" />
              <KPICard label="总 Gross" value={fmt(cur.total_gross_usdt)} suffix="USDT" change={cmp.total_gross_usdt} previous={prev.total_gross_usdt} accent="indigo" big />
              <KPICard label="客户实收 Net" value={fmt(cur.total_net_usdt)} suffix="USDT" change={null} previous={null} accent="indigo" big />
            </div>
          </div>

          {/* Online/Offline split */}
          {(cur.online_count > 0 || cur.offline_count > 0) && (
            <div className="card-base p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-white mb-4">⛺🌐 线下 / 线上 拆分</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-xl bg-indigo-500/10 border border-indigo-500/20 p-4" data-testid="offline-summary">
                  <div className="text-sm text-indigo-300 mb-1">⛺ 线下充值</div>
                  <div className="font-num text-2xl font-bold text-white">{cur.offline_count} <span className="text-sm text-slate-500">笔</span></div>
                  <div className="text-sm text-emerald-400 font-num mt-1">利润 {fmt(cur.offline_profit_usdt)} USDT</div>
                </div>
                <div className="rounded-xl bg-sky-500/10 border border-sky-500/20 p-4" data-testid="online-summary">
                  <div className="text-sm text-sky-300 mb-1">🌐 线上充值</div>
                  <div className="font-num text-2xl font-bold text-white">{cur.online_count} <span className="text-sm text-slate-500">笔</span></div>
                  <div className="text-sm text-emerald-400 font-num mt-1">利润 {fmt(cur.online_profit_usdt)} USDT · Gross {fmt(cur.online_gross_usdt)}</div>
                </div>
              </div>
            </div>
          )}

          {/* 利润 Section */}
          <div className="card-base p-5 sm:p-6">
            <h2 className="text-lg font-semibold text-white mb-4">💰 利润</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <KPICard label="订单利润" value={fmt(cur.order_profit_usdt)} suffix="USDT"
                       change={cmp.order_profit_usdt} previous={prev.order_profit_usdt} accent="emerald" big
                       sub={`汇率 ${fmt(cur.exchange_profit_usdt)} + 手续费 ${fmt(cur.fee_profit_usdt)}`} />
              <KPICard label="银行利润 (结算)" value={fmt(cur.bank_profit_usdt)} suffix="USDT"
                       change={cmp.bank_profit_usdt} previous={prev.bank_profit_usdt} accent="cyan" big
                       sub="基于实际余额对账估算" />
              <KPICard label="开销总额" value={`RM ${fmt(cur.expense_total_myr)}`}
                       change={cmp.expense_total_myr} previous={prev.expense_total_myr} accent="rose" big invertColor />
            </div>
          </div>

          {/* 综合结算 */}
          <div className="card-base p-5 sm:p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <ChartDonut size={20} weight="duotone" className="text-indigo-400" /> {periodLabel} 综合结算
            </h2>
            <div className="space-y-3">
              <Row label="📈 订单笔数" value={cur.order_count + ' 笔'} />
              <Row label="💹 汇率利润 (USDT)" value={fmt(cur.exchange_profit_usdt)} tone="emerald" />
              <Row label="🧾 手续费利润 (USDT)" value={fmt(cur.fee_profit_usdt)} tone="emerald" />
              <Row label="🏦 银行利润 (USDT)" value={fmt(cur.bank_profit_usdt)} tone="emerald" />
              <Row label="💸 银行充值成本 (USD)" value={fmt(cur.bank_deposit_cost_usd)} tone="rose" />
              <div className="h-px bg-slate-700/50 my-3" />
              <Row label="毛利润 (折算 MYR ≈)" value={`RM ${fmt(grossProfitMyr)}`} />
              <Row label={`${mode === 'month' ? '本月' : '本周期'}开销`} value={`-RM ${fmt(cur.expense_total_myr)}`} tone="rose" />
              <div className="h-px bg-slate-700/50 my-3" />
              <div className="flex items-center justify-between bg-gradient-to-r from-emerald-500/10 to-transparent rounded-lg p-3 -mx-1">
                <span className="text-base font-semibold text-white">🎯 净利润估算</span>
                <span className={`font-num text-2xl font-bold ${netProfitMyr >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} data-testid="net-profit-myr">
                  RM {fmt(netProfitMyr)}
                </span>
              </div>
              <div className="text-xs text-slate-500">
                ※ {mode === 'month' ? '本月' : '本周期'}加权平均成本汇率:<span className="text-slate-300 font-num">{fmt(usdtToMyr, 4)}</span>
                {cur.min_cost_rate !== cur.max_cost_rate && (
                  <span className="text-slate-600"> · 区间 {fmt(cur.min_cost_rate, 4)} ~ {fmt(cur.max_cost_rate, 4)}</span>
                )}
                。USDT 利润每笔按订单当时汇率精确算,MYR 折算用{mode === 'month' ? '本月' : '本周期'}加权均值。
              </div>
            </div>
          </div>

          {/* Expense breakdown */}
          <div className="card-base p-5 sm:p-6">
            <h2 className="text-lg font-semibold text-white mb-4">开销分类</h2>
            {Object.keys(data.expense_by_category).length === 0 && (
              <div className="text-sm text-slate-500">本周期没有开销</div>
            )}
            <div className="space-y-2">
              {Object.entries(data.expense_by_category).map(([cat, amt]) => (
                <div key={cat} className="flex justify-between items-center p-3 rounded-lg bg-slate-900/40 border border-slate-800">
                  <span className="text-sm text-slate-300">{cat}</span>
                  <span className="font-num text-rose-300">RM {fmt(amt)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function CompareView({ cur, prev, cmp, curMonth, prevMonth }) {
  const rows = [
    { group: '营业规模', label: '订单笔数', cur: cur.order_count, prev: prev.order_count, change: cmp.order_count, suffix: '笔' },
    { group: '营业规模', label: '活跃客户', cur: cur.unique_customers, prev: prev.unique_customers, change: cmp.unique_customers, suffix: '位' },
    { group: '营业规模', label: '总收 MYR', cur: cur.total_myr, prev: prev.total_myr, change: cmp.total_myr, suffix: 'MYR', decimal: true },
    { group: '营业规模', label: '总收 USDT (直收)', cur: cur.total_usdt_in, prev: prev.total_usdt_in, change: cmp.total_usdt_in, suffix: 'USDT', decimal: true },
    { group: '营业规模', label: '总营业额 Gross', cur: cur.total_gross_usdt, prev: prev.total_gross_usdt, change: cmp.total_gross_usdt, suffix: 'USDT', decimal: true, highlight: true },
    { group: '营业规模', label: '客户实收 Net', cur: cur.total_net_usdt, prev: prev.total_net_usdt, change: null, suffix: 'USDT', decimal: true },

    { group: '利润', label: '汇率利润', cur: cur.exchange_profit_usdt, prev: prev.exchange_profit_usdt, change: null, suffix: 'USDT', decimal: true },
    { group: '利润', label: '手续费利润', cur: cur.fee_profit_usdt, prev: prev.fee_profit_usdt, change: null, suffix: 'USDT', decimal: true },
    { group: '利润', label: '订单利润合计', cur: cur.order_profit_usdt, prev: prev.order_profit_usdt, change: cmp.order_profit_usdt, suffix: 'USDT', decimal: true, highlight: true },
    { group: '利润', label: '银行利润 (结算)', cur: cur.bank_profit_usdt, prev: prev.bank_profit_usdt, change: cmp.bank_profit_usdt, suffix: 'USDT', decimal: true },

    { group: '成本', label: '开销总额', cur: cur.expense_total_myr, prev: prev.expense_total_myr, change: cmp.expense_total_myr, suffix: 'MYR', decimal: true, invertColor: true, prefix: 'RM ' },
    { group: '成本', label: '银行充值成本', cur: cur.bank_deposit_cost_usd, prev: prev.bank_deposit_cost_usd, change: null, suffix: 'USD', decimal: true, invertColor: true, prefix: '$' },
  ];

  const groups = [...new Set(rows.map(r => r.group))];

  return (
    <div className="space-y-5">
      <div className="card-base p-5 sm:p-6 bg-gradient-to-br from-indigo-500/5 to-transparent border-indigo-500/20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
          <BigPeriod month={prevMonth} label="上周期" data={prev} />
          <div className="flex md:flex-col items-center justify-center gap-2 text-center">
            <ArrowsLeftRight size={28} weight="duotone" className="text-indigo-400" />
            <div className="text-xs text-slate-500 uppercase tracking-wider">环比</div>
          </div>
          <BigPeriod month={curMonth} label="本周期" data={cur} highlight />
        </div>
      </div>

      {groups.map(group => (
        <div key={group} className="card-base overflow-hidden">
          <div className="px-5 py-3 bg-[#1F2B45] border-b border-slate-700/50">
            <h3 className="text-sm font-semibold text-white">{group}</h3>
          </div>
          <div className="hidden md:block">
            <table className="w-full">
              <thead>
                <tr className="text-xs uppercase text-slate-500 font-semibold tracking-wider border-b border-slate-800">
                  <th className="px-5 py-2.5 text-left">指标</th>
                  <th className="px-5 py-2.5 text-right">上周期</th>
                  <th className="px-5 py-2.5 text-right">本周期</th>
                  <th className="px-5 py-2.5 text-right">差额</th>
                  <th className="px-5 py-2.5 text-right">变化%</th>
                </tr>
              </thead>
              <tbody>
                {rows.filter(r => r.group === group).map((r) => (
                  <CompareRow key={`${group}-${r.label}`} r={r} />
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden divide-y divide-slate-800">
            {rows.filter(r => r.group === group).map((r) => (
              <MobileCompareRow key={`${group}-${r.label}`} r={r} prevMonth={prevMonth} curMonth={curMonth} />
            ))}
          </div>
        </div>
      ))}

      <div className="text-xs text-slate-500 text-center">
        💡 提示:绿色 = 业务增长 · 红色 = 下跌 · 开销/成本类反向(变多变红)
      </div>
    </div>
  );
}

function BigPeriod({ month, label, data, highlight }) {
  return (
    <div className={`p-4 rounded-xl ${highlight ? 'bg-indigo-500/10 border border-indigo-500/30' : 'bg-slate-900/40 border border-slate-800'}`}>
      <div className="label-tiny mb-1">{label} · {month}</div>
      <div className={`font-num text-2xl font-bold ${highlight ? 'text-white' : 'text-slate-300'}`}>
        {data.order_count} <span className="text-sm text-slate-500">笔</span>
      </div>
      <div className="text-xs text-slate-500 font-num mt-1 space-y-0.5">
        <div>{fmt(data.total_myr)} MYR + {fmt(data.total_usdt_in)} USDT</div>
        <div>Gross {fmt(data.total_gross_usdt)} USDT</div>
        <div className="text-emerald-400">利润 {fmt(data.order_profit_usdt + data.bank_profit_usdt)} USDT</div>
      </div>
    </div>
  );
}

function CompareRow({ r }) {
  const formatVal = (v) => {
    if (v === null || v === undefined) return '-';
    const s = r.decimal ? fmt(v) : v;
    return `${r.prefix || ''}${s}`;
  };
  const trend = renderTrend(r.change, r.invertColor);
  const diff = r.change ? r.change.abs : null;

  return (
    <tr className={`border-b border-slate-800/40 last:border-0 hover:bg-slate-800/20 ${r.highlight ? 'bg-slate-900/40' : ''}`}>
      <td className={`px-5 py-3 text-sm ${r.highlight ? 'text-white font-semibold' : 'text-slate-300'}`}>{r.label}</td>
      <td className="px-5 py-3 text-sm text-right font-num text-slate-400">{formatVal(r.prev)} <span className="text-xs text-slate-600">{r.suffix}</span></td>
      <td className={`px-5 py-3 text-sm text-right font-num ${r.highlight ? 'text-white font-semibold' : 'text-slate-200'}`}>
        {formatVal(r.cur)} <span className="text-xs text-slate-600">{r.suffix}</span>
      </td>
      <td className="px-5 py-3 text-sm text-right font-num">
        {r.change ? (
          <span className={diffColor(diff, r.invertColor)}>
            {diff > 0 ? '+' : ''}{r.prefix || ''}{r.decimal ? fmt(diff) : diff}
          </span>
        ) : <span className="text-slate-600">-</span>}
      </td>
      <td className="px-5 py-3 text-right">{trend}</td>
    </tr>
  );
}

function MobileCompareRow({ r, prevMonth, curMonth }) {
  const trend = renderTrend(r.change, r.invertColor);
  const formatVal = (v) => v === null || v === undefined ? '-' : `${r.prefix || ''}${r.decimal ? fmt(v) : v}`;
  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-2">
        <span className={`text-sm ${r.highlight ? 'text-white font-semibold' : 'text-slate-300'}`}>{r.label}</span>
        {trend}
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase text-slate-500">上周期 {prevMonth}</div>
          <div className="font-num text-slate-400 mt-0.5">{formatVal(r.prev)} <span className="text-xs text-slate-600">{r.suffix}</span></div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-slate-500">本周期 {curMonth}</div>
          <div className={`font-num ${r.highlight ? 'text-white font-semibold' : 'text-slate-200'} mt-0.5`}>
            {formatVal(r.cur)} <span className="text-xs text-slate-600">{r.suffix}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderTrend(change, invertColor) {
  if (!change) return <span className="text-xs text-slate-600">-</span>;
  const pct = change.pct;
  const abs = change.abs;
  const flat = abs === 0;
  const up = abs > 0;
  const isGood = invertColor ? !up : up;
  const color = flat ? 'text-slate-400 bg-slate-700/30' :
    isGood ? 'text-emerald-400 bg-emerald-500/10' : 'text-rose-400 bg-rose-500/10';
  const Icon = flat ? Minus : up ? TrendUp : TrendDown;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium font-num ${color}`}>
      <Icon size={12} weight="bold" />
      <span>{pct === null ? '新增' : `${pct > 0 ? '+' : ''}${pct}%`}</span>
    </span>
  );
}

function diffColor(diff, invertColor) {
  if (diff === 0) return 'text-slate-400';
  const positive = diff > 0;
  const isGood = invertColor ? !positive : positive;
  return isGood ? 'text-emerald-400' : 'text-rose-400';
}

function KPICard({ label, value, suffix, change, previous, accent = 'indigo', big, sub, invertColor }) {
  const accents = {
    indigo: 'text-indigo-300', emerald: 'text-emerald-300', amber: 'text-amber-300',
    cyan: 'text-cyan-300', rose: 'text-rose-300',
  };
  const trendEl = change ? renderTrend(change, invertColor) : null;

  return (
    <div className="card-base p-4 bg-slate-900/40">
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="label-tiny">{label}</span>
        {trendEl}
      </div>
      <div className={`font-num font-semibold text-white tracking-tight ${big ? 'text-2xl sm:text-3xl' : 'text-xl'} ${accents[accent]}`}>
        {value}
        {suffix && <span className="text-xs text-slate-500 ml-1.5 font-medium">{suffix}</span>}
      </div>
      {previous !== null && previous !== undefined && (
        <div className="text-xs text-slate-500 mt-1.5 font-num">
          上周期:{typeof previous === 'number' && previous % 1 !== 0 ? fmt(previous) : previous}
          {suffix && ` ${suffix}`}
        </div>
      )}
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function Row({ label, value, tone }) {
  const cls = tone === 'emerald' ? 'text-emerald-400' : tone === 'rose' ? 'text-rose-400' : 'text-white';
  return (
    <div className="flex justify-between items-center">
      <span className="text-sm text-slate-400">{label}</span>
      <span className={`font-num font-semibold ${cls} text-sm`}>{value}</span>
    </div>
  );
}
